import { Inject, Injectable } from "@angular/core";
import { ColumnConfig, ConfirmOptionsModel, NmsTableValidatorService } from "@nms-angular-toolkit/nms-table";
import {
    ANGULARJS_TRANSLATE,
    LABEL_COLOR_SERVICE,
    NMS_FEATURES
} from "@nms-ng2/app/shared/services/upgraded-provider/upgraded-providers";
import { LinksShortcutActionService } from "../../utils/links-shortcut-action.service";
import { LinkService } from "./link.service";
import { LINK_SITUATION, LinkInfo, LinkSituation, PortLinkType, STATUS, Status } from "../links.model";
import { AddLinkModalComponent } from "../modal-wizard/add-link-modal.component";
import { ModalFactory } from "@nms-ng2/app/shared/services/modal/modal.factory";
import { LinkInfoCircuit } from "../../dwdm/circuit/wizard/steps/4-links/links-step.model";

/**
 * Serviço responsável por coerências da tabela de Links.
 */
@Injectable({
    providedIn: "root"
})
export class LinksTableService {

    private readonly COLUMNS_TO_IGNORE_ON_MODAL = ["actions", "typeDescription"];

    private readonly DISTANCE_COLUMN: ColumnConfig = {
        field: "distance",
        label: this.translate.instant("table.links.distance"),
        width: "150px",
        unsortable: true,
        optionalColumn: {
            type: "text",
            textCustomOptions: {
                buildCustomText: (row: LinkInfoCircuit) => {
                    return `${row.distance} km`
                },
                customClass: () => { return "" }
            }
        }
    };

    private readonly ATTENUATION_COLUMN: ColumnConfig = {
        field: "attenuation",
        label: this.translate.instant("table.links.attenuation"),
        width: "150px",
        unsortable: true,
        optionalColumn: {
            type: "text",
            textCustomOptions: {
                buildCustomText: (row: LinkInfoCircuit) => {
                    return `${row.attenuation} dB/km`
                },
                customClass: () => { return "" }
            }
        }
    };

    constructor(@Inject(ANGULARJS_TRANSLATE) private readonly translate,
        @Inject(LABEL_COLOR_SERVICE) private readonly labelColorService: any,
        @Inject(NMS_FEATURES) private readonly nmsFeatures: any,
        private readonly linksShortcutActionService: LinksShortcutActionService,
        private readonly linkService: LinkService,
        private readonly nmsTableValidatorService: NmsTableValidatorService,
        private readonly modalFactory: ModalFactory) { }

    getColumns(isOpenInModal: boolean): ColumnConfig[] {
        const columns: ColumnConfig[] = [
            {
                field: "name",
                label: this.translate.instant("table.links.name"),
                minWidth: "500px",
                width: "535px",
                optionalColumn: {
                    type: "text",
                    onAction: (linkInfo: LinkInfo) => {
                        this.editInline(linkInfo);
                    }
                },
                alwaysVisible: true
            },
            { field: "aEnd", label: this.translate.instant("table.links.aEnd"), minWidth: "200px", width: "300px" },
            { field: "zEnd", label: this.translate.instant("table.links.zEnd"), minWidth: "200px", width: "300px" },
            {
                field: "typeDescription",
                label: this.translate.instant("table.links.type"),
                minWidth: "150px",
                width: "150px",
                availableValues: [
                    { value: PortLinkType.AGG_ETH, label: PortLinkType.AGG_ETH },
                    { value: PortLinkType.EM, label: PortLinkType.EM },
                    { value: PortLinkType.E1, label: PortLinkType.E1 },
                    { value: PortLinkType.E3, label: PortLinkType.E3 },
                    { value: PortLinkType.ETHERNET, label: PortLinkType.ETHERNET },
                    { value: PortLinkType.GSHDSL_EFM, label: PortLinkType.GSHDSL_EFM },
                    { value: PortLinkType.GSHDSL_TDM, label: PortLinkType.GSHDSL_TDM },
                    { value: PortLinkType.G64, label: PortLinkType.G64 },
                    { value: PortLinkType.MBB, label: PortLinkType.MBB },
                    { value: PortLinkType.PON, label: PortLinkType.PON },
                    { value: PortLinkType.STM1, label: PortLinkType.STM1 },
                    { value: PortLinkType.STM4, label: PortLinkType.STM4 },
                    { value: PortLinkType.STM16, label: PortLinkType.STM16 },
                    { value: PortLinkType.STM64, label: PortLinkType.STM64 },
                    { value: PortLinkType.V28, label: PortLinkType.V28 },
                    { value: PortLinkType.V35, label: PortLinkType.V35 }
                ],
                optionalColumn: {
                    type: "enum",
                    convert: (value) => {
                        return { label: value };
                    }
                }
            },
            {
                field: "linkStatus",
                label: this.translate.instant("table.links.status"),
                minWidth: "150px",
                width: "150px",
                availableValues: [
                    { value: String(Status.CRITICAL_FAULT), label: this.translate.instant("status.criticalFault") },
                    { value: String(Status.MAJOR_FAULT), label: this.translate.instant("status.majorFault") },
                    { value: String(Status.MINOR_FAULT), label: this.translate.instant("status.minorFault") },
                    { value: String(Status.TEST), label: this.translate.instant("status.test") },
                    { value: String(Status.DISABLED), label: this.translate.instant("status.disabled") },
                    { value: String(Status.NORMAL), label: this.translate.instant("status.normal") },
                    { value: String(Status.UNKNOWN), label: this.translate.instant("status.unknown") }
                ],
                optionalColumn: {
                    type: "status",
                    convert: (status) => {
                        const { translationKey, bgColor } = STATUS[status.trim().toUpperCase()];
                        const color = this.labelColorService.getBestColorContrastForForeground(bgColor);
                        const label = this.translate.instant(translationKey);

                        return { label, bgColor, color };
                    }
                }
            },
            {
                field: "linkSituation",
                label: this.translate.instant("table.links.situation"),
                minWidth: "150px",
                width: "150px",
                optionalColumn: {
                    type: "enum",
                    convert: (value) => {
                        const { translationKey } = LINK_SITUATION[value.trim().toUpperCase()];
                        const label = this.translate.instant(translationKey);

                        return { label };
                    }
                },
                availableValues: [
                    { value: String(LinkSituation.ACTIVE), label: this.translate.instant("link.situation.active") },
                    { value: String(LinkSituation.NOT_ACTIVE), label: this.translate.instant("link.situation.notActive") }
                ],
                show: !isOpenInModal
            },
            { field: "weight", label: this.translate.instant("table.links.weight"), show: false, width: "100px" },
            {
                field: "actions",
                label: this.translate.instant("table.actions"),
                optionalColumn: {
                    type: "actions"
                },
                width: "5%",
                unsortable: true,
                unsearchable: true,
                actions: [
                    {
                        id: "edit",
                        iconName: "edit_square",
                        tooltip: this.translate.instant("tableLinks.edit"),
                        onAction: (linkInfo: LinkInfo) => {
                            this.editInline(linkInfo);
                        }
                    },
                    {
                        id: "delete",
                        iconName: "delete",
                        tooltip: this.translate.instant("tableLinks.delete"),
                        onAction: (linkInfo: LinkInfo) => {
                            this.deleteInline(linkInfo);
                        }
                    }
                ]
            }
        ];

        if (isOpenInModal) {
            const filteredColumns = columns.filter((column) => !this.COLUMNS_TO_IGNORE_ON_MODAL.includes(column.field));
            filteredColumns.splice(3, 0, this.DISTANCE_COLUMN);
            filteredColumns.splice(4, 0, this.ATTENUATION_COLUMN);
            filteredColumns.forEach((column) => {
                column.unsearchable = true;

                if (column.field === "name") {
                    column.optionalColumn = {
                        type: "text",
                        textCustomOptions: {
                            customClass: (row: LinkInfo) => {
                                return "customBold500Text"
                            },
                            buildCustomText: (row: LinkInfo) => {
                                return row.name
                            }
                        }
                    }
                }
            });

            return filteredColumns;
        }

        return columns;
    }

    getMessageForConfirmOptions(rowsSelectedLength: number): ConfirmOptionsModel {
        const message = rowsSelectedLength <= 1
            ? this.translate.instant("table.links.confirm.remove.singleDescription")
            : this.translate.instant("table.links.confirm.remove.pluralDescription");

        return {
            title: this.translate.instant("table.links.confirm.remove.title"),
            description: message
        } as ConfirmOptionsModel;
    }

    private editInline(linkInfo: LinkInfo) {
        if (this.linksShortcutActionService.userHasPermission(this.nmsFeatures.linkConfiguration.feature)) {
            this.linkService.getLinkById(linkInfo.id).subscribe(link => {
                this.modalFactory.openAsyncModal(AddLinkModalComponent, { linkInfo: link });
            });
        }
    }

    private deleteInline(linkInfo: LinkInfo, rowsSelectedLength: number = 1) {
        if (this.linksShortcutActionService.userHasPermission(this.nmsFeatures.removeLinkFromNms.feature)) {
            this.nmsTableValidatorService.showConfirmOptions(this.getMessageForConfirmOptions(rowsSelectedLength)).then(() => {
                this.linkService.removeLinks([linkInfo]);
            });
        }
    }
}
